import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Modal, CheckBox, InputNumber, Radio } from '@roo/roo';
import { failToast, warningToast, successToast } from '@utils/toast';
import request from '@utils/axiosWrapper';
import styles from './ResendFoodModal.module.scss';

const CheckBoxGroup = (CheckBox as any).Group;

/** 商品详情 */
type CartDetailInfo = {
    /** 篮子名称 */
    cartName: string;
    /** 篮子内的菜品详情 */
    details: FoodCartDetailInfo[];
};
/** 商品篮子详情 */
type FoodCartDetailInfo = {
    /** 所属篮子 id */
    cartId: number;
    foodId: number;
    /** 菜品名称 */
    foodName: string;
    /** 菜品单价 折扣价 */
    foodPrice: number;
    /** 已点份数 */
    count: number;
    /** 菜品原价 */
    originFoodPrice: number;
    /** 折扣商品的打折数 */
    discountPoint: string;
    /** 套餐 */
    subDetails: {
        /** 套餐餐品名 */
        foodName: string;
        /** 数量 */
        count: number;
    }[];
    /** 是否是神抢手商品 */
    isSharpShooter: boolean;

    detailId: string;
};

interface ResendMealFoodInfo {
    wmFoodId: string;
    foodName: string;
    count: number;
}

interface CartProps {
    cartName: string;
    foods: FoodCartDetailInfo[];
    value: {
        /** 选中的 foodId */
        selectedFoodIds: string[];
        /** 每一项代表该位置的 item 选中的数量 */
        selectedCounts: number[];
    };
    onChange: (value: CartProps['value']) => void;
}

const Cart: React.FC<CartProps> = ({ cartName, foods, value, onChange }) => {
    const { selectedFoodIds, selectedCounts } = value;

    return (
        <div className={styles.cart}>
            <div className={styles.cartTitle}>
                <CheckBox
                    indeterminate={selectedFoodIds.length > 0}
                    checked={selectedFoodIds.length === foods.length}
                    onChange={() => {
                        if (selectedFoodIds.length === foods.length) {
                            onChange({ ...value, selectedFoodIds: [] });
                        } else {
                            onChange({
                                ...value,
                                selectedFoodIds: foods.map(food => String(food.foodId)),
                            });
                        }
                    }}
                />
                <span className={styles.cartName}>
                    <img src={require('./assets/cart.png')} />
                    <span>{cartName}</span>
                </span>
            </div>

            <div className={styles.cartFood}>
                <CheckBoxGroup
                    value={selectedFoodIds}
                    onChange={(foodIds: string[]) => {
                        onChange({ ...value, selectedFoodIds: foodIds });
                    }}
                >
                    {foods.map((food, index) => {
                        return (
                            <div className={styles.foodItemWrapper} key={food.foodId}>
                                <div style={{ flexShrink: 0 }}>
                                    <CheckBox value={String(food.foodId)} />
                                </div>
                                <div className={styles.foodItem}>
                                    <div className={styles.foodInfo}>
                                        <div className={styles.cartFoodInfo}>
                                            <div>
                                                {food.foodPrice !== food.originFoodPrice ? (
                                                    <span className={styles.label}>折</span>
                                                ) : null}
                                                {food.foodName}
                                            </div>
                                            {food.subDetails?.length ? (
                                                <div className={styles.subDetail}>
                                                    {food.subDetails
                                                        .map(
                                                            subDetail =>
                                                                `${subDetail.foodName} x${subDetail.count}`,
                                                        )
                                                        .join('，')}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className={styles.foodCountInfo}>
                                        <div className={styles.cartFoodCount}>x{food.count}</div>
                                        <InputNumber
                                            value={selectedCounts[index]}
                                            onChange={newCount => {
                                                const newSelectedCounts = [...selectedCounts];
                                                newSelectedCounts[index] = Number(newCount);
                                                onChange({
                                                    ...value,
                                                    selectedCounts: newSelectedCounts,
                                                });
                                            }}
                                            min={1}
                                            max={food.count}
                                            editable={false}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </CheckBoxGroup>
            </div>
        </div>
    );
};

const ResendFoodModal: React.FC<{
    onCancel: () => void;
    carts: CartDetailInfo[];
    visible: boolean;
    wmPoiId: string;
    wmOrderViewId: string;
}> = ({ onCancel, carts, visible, wmPoiId, wmOrderViewId }) => {
    const [selectedFoodIdsList, setSelectedItemIdsList] = useState<string[][]>();
    const [selectedCountsList, setSelectedCountsList] = useState<number[][]>();
    useEffect(() => {
        setSelectedItemIdsList(
            carts.map(() => {
                return [];
            }),
        );
        setSelectedCountsList(
            carts.map(cart => {
                return Array.from<number>({ length: cart.details.length }).fill(1);
            }),
        );
    }, [carts]);

    const flatFoodInfo = useMemo<ResendMealFoodInfo[]>(() => {
        const _flatFoodInfo: ResendMealFoodInfo[] = [];
        carts.forEach((cart, cartIndex) => {
            const selectedItemIds = selectedFoodIdsList?.[cartIndex];
            const selectedCounts = selectedCountsList?.[cartIndex];

            if (selectedItemIds && selectedCounts) {
                cart.details.forEach((food, foodIndex) => {
                    if (selectedItemIds?.includes(String(food.foodId))) {
                        _flatFoodInfo.push({
                            wmFoodId: food.detailId,
                            foodName: food.foodName,
                            count: selectedCounts[foodIndex],
                        });
                    }
                });
            }
        });
        return _flatFoodInfo;
    }, [carts, selectedFoodIdsList, selectedCountsList]);

    const totalCount = useMemo(
        () =>
            flatFoodInfo.reduce((prev, cur) => {
                return prev + cur.count;
            }, 0),
        [flatFoodInfo],
    );

    const submitting = useRef(false);

    return (
        <Modal
            visible={visible}
            title="补发餐品"
            onCancel={onCancel}
            className={styles.resendFoodModal}
            showConfirmButton={false}
            showCancelButton={false}
            closable
        >
            <div>
                <div className={styles.header}>
                    <img className={styles.guideIcon} src={require('./assets/guide.png')} />
                    <div className={styles.content}>
                        操作前请务必在电话或聊天中与顾客协商并征得其同意，请和顾客协商时间并尽快配送。
                    </div>
                </div>
                <div className={styles.body}>
                    <div className={styles.foodInfo}>
                        <div className={styles.title}>
                            <span>*</span>请选择补发商品
                        </div>
                        <div>
                            {carts.map(({ cartName, details }, index) => {
                                if (!details?.length) return null;
                                if (!selectedFoodIdsList?.[index] || !selectedCountsList?.[index])
                                    return null;

                                return (
                                    <Cart
                                        key={details[0].cartId}
                                        cartName={cartName}
                                        foods={details}
                                        value={{
                                            selectedFoodIds: selectedFoodIdsList[index],
                                            selectedCounts: selectedCountsList[index],
                                        }}
                                        onChange={({ selectedFoodIds, selectedCounts }) => {
                                            const newSelectedItemIdsList = [...selectedFoodIdsList];
                                            newSelectedItemIdsList[index] = selectedFoodIds;
                                            setSelectedItemIdsList(newSelectedItemIdsList);

                                            const newSelectedCountsList = [...selectedCountsList];
                                            newSelectedCountsList[index] = selectedCounts;
                                            setSelectedCountsList(newSelectedCountsList);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.resendPlan}>
                        <div className={styles.title}>
                            <span>*</span>请选择补发配送方式
                        </div>
                        <div>
                            <Radio checked disabled>
                                <span className={styles.resendPlanOption}>
                                    <span className={styles.resendPlanOptionTitle}>
                                        商家自己送餐
                                    </span>
                                    <span className={styles.resendPlanOptionRemark}>
                                        您需要自行配送或采用三方运力配送完成餐品的补发
                                    </span>
                                </span>
                            </Radio>
                        </div>
                    </div>
                </div>
                <div className={styles.footer}>
                    <div className={styles.text}>
                        <div>已选补发餐品</div>
                        <div style={{ fontSize: 20, marginTop: 5 }}>{totalCount}件</div>
                    </div>
                    <div className={styles.buttonGroup}>
                        <Button
                            onClick={() => {
                                if (submitting.current) return;
                                submitting.current = true;

                                if (flatFoodInfo.length === 0) {
                                    warningToast('请至少选择一个需要补发的商品');
                                    submitting.current = false;
                                    return;
                                }

                                request
                                    .rawPostAsFormSubmit<{ code: number; message: string }>(
                                        '/gw/api/order/afterSale/recover/resendMeal',
                                        {
                                            wmPoiId,
                                            wmOrderViewId,
                                            foodInfos: JSON.stringify(flatFoodInfo),
                                            channelType: 1,
                                        },
                                    )
                                    .then(res => {
                                        if (res.code === 0) {
                                            successToast('已成功补发餐品');
                                            onCancel();
                                        } else {
                                            failToast(res.message);
                                        }
                                        submitting.current = false;
                                    })
                                    .catch(err => {
                                        failToast(err.message);
                                        submitting.current = false;
                                    });
                            }}
                        >
                            补发餐品
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ResendFoodModal;
export { CartDetailInfo };
